import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Settings } from "lucide-react";
import { supabase } from "@/supabase";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import AuthGuard from "@/components/admin/AuthGuard";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: "",
    whatsapp_message: "",
    instagram: "",
    pagseguro_email: "",
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settingsData } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("*");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (settingsData) {
      const settingsMap: Record<string, string> = {};
      settingsData.forEach((s) => {
        settingsMap[s.setting_key] = s.setting_value || "";
      });
      setSettings({
        whatsapp_number: settingsMap.whatsapp_number || "",
        whatsapp_message: settingsMap.whatsapp_message || "",
        instagram: settingsMap.instagram || "",
        pagseguro_email: settingsMap.pagseguro_email || "",
      });
    }
  }, [settingsData]);

  const updateMutation = useMutation({
    mutationFn: async (newSettings: typeof settings) => {
      const updates = Object.entries(newSettings).map(([key, value]) => ({
        setting_key: key,
        setting_value: value,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from("site_settings")
          .update({ setting_value: update.setting_value })
          .eq("setting_key", update.setting_key);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
      toast({ title: "Configurações salvas com sucesso!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao salvar configurações",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(settings);
  };

  return (
    <AuthGuard>
      <AdminLayout>
        <div className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-foreground">
            Configurações
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure as informações gerais da sua loja
          </p>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* WhatsApp Settings */}
            <div className="admin-card">
              <h2 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                WhatsApp
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="admin-label">Número do WhatsApp</label>
                  <input
                    type="text"
                    value={settings.whatsapp_number}
                    onChange={(e) =>
                      setSettings({ ...settings, whatsapp_number: e.target.value })
                    }
                    className="admin-input"
                    placeholder="5511999999999"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Digite apenas números, incluindo código do país (55) e DDD
                  </p>
                </div>
                <div>
                  <label className="admin-label">Mensagem Padrão</label>
                  <textarea
                    value={settings.whatsapp_message}
                    onChange={(e) =>
                      setSettings({ ...settings, whatsapp_message: e.target.value })
                    }
                    className="admin-input min-h-[80px]"
                    rows={3}
                    placeholder="Olá! Gostaria de saber mais sobre..."
                  />
                </div>
              </div>
            </div>

            {/* Social Settings */}
            <div className="admin-card">
              <h2 className="font-semibold text-lg text-foreground mb-4">Redes Sociais</h2>
              <div>
                <label className="admin-label">Instagram</label>
                <input
                  type="text"
                  value={settings.instagram}
                  onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                  className="admin-input"
                  placeholder="@solarcrocheteria"
                />
              </div>
            </div>

            {/* Payment Settings */}
            <div className="admin-card">
              <h2 className="font-semibold text-lg text-foreground mb-4">Pagamento</h2>
              <div>
                <label className="admin-label">E-mail PagSeguro</label>
                <input
                  type="email"
                  value={settings.pagseguro_email}
                  onChange={(e) =>
                    setSettings({ ...settings, pagseguro_email: e.target.value })
                  }
                  className="admin-input"
                  placeholder="seu@email.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  E-mail da sua conta PagSeguro para receber pagamentos
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {updateMutation.isPending ? "Salvando..." : "Salvar Configurações"}
            </button>
          </form>
        </div>
      </AdminLayout>
    </AuthGuard>
  );
};

export default AdminSettings;
